<template>
  <ion-content class="text-selectable modal-content">
    <div class="content-wrapper">
      <div class="scrollable-content">
        <div class="info-section">
          <div class="dotation-header">
            <div class="dotation-icon">
              <font-awesome-icon :icon="['fas', 'gift']" />
            </div>
            <div class="dotation-details">
              <h2 class="dotation-title">{{ t("settings-page.send-dotations") }}</h2>
              <div class="dotation-subtitle">{{ t("settings-page.dotations-description") }}</div>
            </div>
          </div>
        </div>

        <div class="form-section">
          <div class="form-card">
            <div class="year-selector">
              <h4>{{ t("settings-page.select-year") }}</h4>
              <ion-select v-model="selectedYear" interface="action-sheet" :placeholder="t('settings-page.select-year')">
                <ion-select-option v-for="year in availableYears" :key="year" :value="year">
                  {{ year }}
                </ion-select-option>
              </ion-select>
            </div>
          </div>
        </div>
      </div>

      <div class="action-section">
        <ion-button expand="block" size="large" class="primary-action" @click="onSend()" :disabled="sendInProgress">
          <font-awesome-icon :icon="['fas', 'paper-plane']" class="button-icon" />
          {{ t("common.send") }}
        </ion-button>
      </div>
    </div>
  </ion-content>
</template>

<script lang="ts">
import { useUserTalent } from "@/hooks/talent";
import errorHandlerService from "@/services/error-handler.service";
import userService from "@/services/user.service";
import {
  IonContent,
  IonButton,
  IonSelect,
  IonSelectOption,
  modalController,
  toastController,
} from "@ionic/vue";
import { DateTime } from "luxon";
import { defineComponent, ref, computed } from "vue";
import { useI18n } from "vue-i18n";

export default defineComponent({
  name: "SendDotationsModal",
  components: {
    IonContent,
    IonButton,
    IonSelect,
    IonSelectOption,
  },
  setup() {
    const { t } = useI18n();
    const sendInProgress = ref<boolean>(false);
    const currentYear = DateTime.now().year;
    const selectedYear = ref<number>(currentYear - 1);

    useUserTalent();

    const availableYears = computed(() => {
      const years: number[] = [];
      for (let year = 2021; year <= currentYear; year++) {
        years.push(year);
      }
      return years.reverse();
    });

    const onSend = async () => {
      try {
        sendInProgress.value = true;

        await userService.sendDotations({ year: selectedYear.value });

        (
            await toastController.create({
              message: t("settings-page.dotations-email-sent"),
              position: "top",
              duration: 1000,
              color: "primary",
            })
        ).present();
        modalController.dismiss();
      } catch (error: any) {
        errorHandlerService.handleApiError(error);
      } finally {
        sendInProgress.value = false;
      }
    };

    return {
      t,
      selectedYear,
      availableYears,
      sendInProgress,
      onSend,
    };
  },
});
</script>

<style scoped>
/* Content Wrapper */
.content-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 0;
}

.scrollable-content {
  flex: 1;
  padding: 10px;
}

/* Info Section */
.info-section {
  margin-bottom: 24px;
}

.dotation-header {
  display: flex;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid var(--loopin-color-gs-30);
}

.dotation-icon {
  width: 48px;
  height: 48px;
  margin-right: 16px;
  border-radius: 50%;
  background: var(--loopin-color-blue);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
}

.dotation-details {
  flex: 1;
}

.dotation-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 4px 0;
  line-height: 1.3;
  color: var(--loopin-color-gs-100);
}

.dotation-subtitle {
  font-size: 14px;
  color: var(--loopin-color-gs-70);
  margin: 0;
}

/* Form Section */
.form-section {
  margin-bottom: 16px;
}

.form-card {
  background: var(--loopin-item-background);
  border-radius: 12px;
  padding: 20px;
}

.year-selector h4 {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: var(--loopin-color-gs-100);
}

ion-select {
  width: 100%;
  --padding-start: 12px;
  --padding-end: 12px;
  border: 1px solid var(--loopin-color-gs-30);
  border-radius: 8px;
  background: var(--loopin-color-gs-10);
}

/* Action Section */
.action-section {
  padding: 16px 10px 24px;
  background: var(--loopin-color-gs-10);
  border-top: 1px solid var(--loopin-color-gs-30);
}

.primary-action {
  --background: var(--loopin-color-blue);
  --background-hover: var(--loopin-color-blue-dark);
  --border-radius: 12px;
  height: 48px;
  font-weight: 600;
  font-size: 16px;
}

.button-icon {
  margin-right: 8px;
  font-size: 14px;
}

/* Modal Content */
.modal-content {
  --background: var(--loopin-color-gs-10);
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .dotation-title {
    font-size: 18px;
  }

  .form-card {
    padding: 16px;
  }

  .action-section {
    padding: 16px 10px;
  }
}
</style>
